'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CountryCode } from '@sola/schema/dotcom/shared/geo.ts';

export const postalCodePatternUS = /^\d{5}((-|(\s*))?\d{4})?$/;
export const postalCodeMaskMX = /^\d{5}$/;
export const postalCodePatternCanada = /^[a-zA-Z]\d[a-zA-Z] ?\d[a-zA-Z]\d$/;

/**
 * Matches characters NOT allowed in name fields.
 * Uses Unicode property escapes to support accented and non-Latin characters
 * (e.g. José, Müller, Nūñez, CJK, Cyrillic). Accepts letters, combining marks,
 * digits, straight/curly apostrophes, ampersand, space, and hyphen.
 */
export const illegalNameCharsRegex = /[^\p{L}\p{M}0-9'\u2019 &-]/gu;

const datePatterns = {
  usAndEs: /^\d{2}\/\d{2}\/\d{4}$/,
  ko: /^\d{4}\.\d{2}\.\d{2}$/,
};

export type GeneratePostalPatternFunc = (countryCode: CountryCode) => {
  value: RegExp;
  message: string;
} | null;

/**
 * Hook for generating postal code patterns based on country ISO code.
 */
export const usePostalPattern = (countryCode: CountryCode) => {
  const { t } = useTranslation(['forms']);
  const [generatePattern, setGeneratePattern] =
    useState<GeneratePostalPatternFunc | null>(null);

  /**
   * Function to lazy load the pattern assets.
   * This will only be called once, and serves to load pattern dependencies that are needed
   * for the generatePattern function, which can then be subsequently used to dynamically generate
   * a patterns, placeholders, and validations based on country code.
   *
   * @TODO Don't forget to check how these imports are cached to make sure browser isn't downloading a unique bundle every time.
   *
   */
  const loadPattern = useCallback(async () => {
    const generatePatternFunc = (countryCodeToPattern: CountryCode) => {
      switch (countryCodeToPattern) {
        case 'US':
          return {
            value: postalCodePatternUS,
            message: t('forms:errors.invalidZipCode'),
          };
        case 'MX':
          return {
            value: postalCodeMaskMX,
            message: t('forms:errors.invalidPostalCodeMexico'),
          };
        case 'CA':
          return {
            value: postalCodePatternCanada,
            message: t('forms:errors.invalidPostalCodeCanada'),
          };
        default:
          return null;
      }
    };

    setGeneratePattern(() => generatePatternFunc);
  }, []);

  /**
   * Generate a pattern anytime the country code changes.
   */
  const pattern = useMemo(() => {
    if (!generatePattern) {
      return;
    }
    return generatePattern(countryCode);
  }, [countryCode, generatePattern]);

  return {
    loadPattern,
    pattern,
  };
};

type GenerateDatePatternFunc = (localeCode: string) => RegExp | null;

/**
 * Hook for generating date patterns based on locale.
 */
export const useDatePattern = (localeCode: string) => {
  const { t } = useTranslation();
  const [generatePattern, setGeneratePattern] =
    useState<GenerateDatePatternFunc | null>(null);

  /**
   * Function to lazy load the pattern assets.
   * This will only be called once, and serves to load pattern dependencies that are needed
   * for the generatePattern function, which can then be subsequently used to dynamically generate
   * a patterns, placeholders, and validations based on country code.
   *
   * @TODO Don't forget to check how these imports are cached to make sure browser isn't downloading a unique bundle every time.
   *
   */
  const loadPattern = useCallback(async () => {
    const generatePatternFunc = (localeCodeToPattern: string) => {
      switch (localeCodeToPattern) {
        case 'ko':
          return datePatterns.ko;
        default:
          return datePatterns.usAndEs;
      }
    };

    setGeneratePattern(() => generatePatternFunc);
  }, []);

  /**
   * Generate a pattern anytime the country code changes.
   */
  const pattern = useMemo(() => {
    if (!generatePattern) {
      return;
    }
    return generatePattern(localeCode);
  }, [localeCode, generatePattern]);

  return {
    loadPattern,
    pattern,
  };
};

export const removeIllegalChars = (
  event: React.ChangeEvent<HTMLInputElement>,
  regex: RegExp,
) => {
  const { currentTarget } = event;
  const original = currentTarget.value;

  // Number fields have their own formatting (e.g. 22 → 22.00) that shifts
  // the value length independently — preserve the legacy cursor offset for them.
  if (currentTarget.type === 'number') {
    const matches = (original.match(regex) || []).length;
    if (matches > 0) {
      currentTarget.value = original.replace(regex, '');
    }
    return;
  }

  const cleaned = original.replace(regex, '');

  if (original.length === cleaned.length) {
    return;
  }

  const cursorBefore = currentTarget.selectionStart ?? original.length;
  // Count how many illegal chars were before the cursor
  const beforeCursor = original.slice(0, cursorBefore);
  const charsRemovedBeforeCursor =
    beforeCursor.length - beforeCursor.replace(regex, '').length;

  currentTarget.value = cleaned;

  // Place cursor where it would naturally land after stripping characters
  const newPosition = cursorBefore - charsRemovedBeforeCursor;
  currentTarget.selectionStart = newPosition;
  currentTarget.selectionEnd = newPosition;
};
